import type { PaymentProvider } from "@repo/types";
import { type Till } from "~/api/tills/types";
import { getEmployee } from "~/providers/store/employee";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";

export type InferPaymentButtonsArgs = {
  till: Till;

  unitPriceBasketTotalInclVat: number;
  unitPriceBalanceToPayInclVat: number;
  unitPriceBalance: number | null;
  unitPriceCollectAvailableInclVat: number | null;

  isUserAuthenticated: boolean;
  isSplitPaymentActive: boolean;
  isBasketEmpty: boolean;

  hasCashDrawer: boolean;
};

const paymentTypes = [
  "billing",
  "card",
  "cash",
  "mobilepay_own",
  "credit",
  "zero_payment",
  "swish"
] as const;

type PaymentProviders = (typeof paymentTypes)[number];

type InferPaymentButtonsReturn =
  | {
      type: "single";
      paymentOption: Extract<
        PaymentProviders,
        "billing" | "credit" | "zero_payment"
      >;
    }
  | {
      type: "multiple";
      paymentOptions: PaymentProviders[];
    }
  | {
      type: "none";
    }
  | {
      type: "empty-basket";
    }
  | {
      type: "insufficient-balance";
    };

export function inferPaymentButtons({
  till,
  unitPriceBasketTotalInclVat,
  unitPriceBalanceToPayInclVat,
  unitPriceBalance,
  unitPriceCollectAvailableInclVat,
  isBasketEmpty,
  isSplitPaymentActive,
  isUserAuthenticated,
  hasCashDrawer
}: InferPaymentButtonsArgs): InferPaymentButtonsReturn {
  const { groupName } = getEmployee();

  const unitPriceCardAmountInclVat = Math.max(
    0,
    unitPriceBasketTotalInclVat - unitPriceBalanceToPayInclVat
  );

  const isReimbursement = unitPriceBasketTotalInclVat < 0;

  const isFullyHybridBilled =
    till.type === "self-service" &&
    till.moduleConfig.hasHybridBilling &&
    unitPriceBasketTotalInclVat === 0 &&
    !isBasketEmpty;

  const isGuest =
    till.type === "self-service" && till.config.guestSelector === groupName;

  const provider = till.paymentGatewayConfig?.provider;
  const isBillingTill = till.moduleConfig.paymentMethod === "billing";

  const isFreeOrder = unitPriceBasketTotalInclVat === 0;
  const isSettledViaBalance = unitPriceBalanceToPayInclVat > 0;
  const requiresBillingFirst = isBillingTill && isSettledViaBalance;
  const splitAllowsOffTerminal = !isSplitPaymentActive || isFreeOrder;

  const isFullyCoveredOffTerminal =
    !isBasketEmpty &&
    !isReimbursement &&
    splitAllowsOffTerminal &&
    unitPriceCardAmountInclVat === 0 &&
    !requiresBillingFirst;

  // Before the guest branch: a guest free order must not reach the card terminal
  if (isFullyCoveredOffTerminal) {
    return {
      type: "single",
      paymentOption: isSettledViaBalance ? "credit" : "zero_payment"
    };
  }

  // When payment method is billing and the user is a guest, we want to show only the card payment button
  if (isBillingTill && isGuest) {
    return {
      type: "multiple",
      paymentOptions: ["card"]
    };
  }

  // When payment method is billing, we want to hide all payment buttons except billing
  // When is self-service and has hybrid billing and the basket total is 0, we want to hide all payment buttons except billing, even if the module is credit type.
  if ((isBillingTill || isFullyHybridBilled) && !isReimbursement) {
    return {
      type: "single",
      paymentOption: "billing"
    };
  }

  if (
    unitPriceBalance &&
    unitPriceBalance >= unitPriceBasketTotalInclVat &&
    (unitPriceCollectAvailableInclVat === null ||
      unitPriceCollectAvailableInclVat >= unitPriceBasketTotalInclVat)
  ) {
    return {
      type: "single",
      paymentOption: "credit"
    };
  }

  const tillIncludesPaymentProvider = (paymentProvider: PaymentProvider) =>
    till.paymentProviders?.includes(paymentProvider);

  const paymentOptions: PaymentProviders[] = [];

  if (tillIncludesPaymentProvider("card") && Boolean(provider)) {
    paymentOptions.push("card");
  }

  if (tillIncludesPaymentProvider("swish") && provider === "adyen") {
    paymentOptions.push("swish");
  }

  if (
    tillIncludesPaymentProvider("cash") &&
    hasCashDrawer &&
    hasAttendedSellConfig(till) &&
    !isSplitPaymentActive
  ) {
    paymentOptions.push("cash");
  }

  if (tillIncludesPaymentProvider("mobilepay_own") && !isReimbursement) {
    paymentOptions.push("mobilepay_own");
  }

  if (paymentOptions.length === 0) {
    if (isBasketEmpty) {
      return {
        type: "empty-basket"
      };
    }

    return {
      type: isUserAuthenticated ? "insufficient-balance" : "none"
    };
  }

  return {
    type: "multiple",
    paymentOptions
  };
}
